/**
 * A board's stored colour, made safe to draw (owner, 19.10.2026: a board made
 * over MCP showed "without bg").
 *
 * The overview tile paints the colour as `background-image`
 * (BoardOverviewTileFull.css), so only a gradient works there. A plain colour
 * that an agent or another client stored ("#22c55e") is an invalid image, and
 * the tile was left with no background at all.
 *
 * Writers (MCP board_create / board_update, the in-app AI) store
 * `normalizeBoardColor(...)`: a gradient as-is, a hex colour as a gradient of
 * that colour, anything else as null. Readers draw `boardBackground(...)`, which
 * also covers records written before this, with the default gradient as the
 * fallback.
 *
 * Pure and store-free, so the MCP handlers can import it.
 */
export const DEFAULT_BOARD_GRADIENT = 'linear-gradient(135deg, #2563EB, #9333EA)';

const GRADIENT = /^(repeating-)?(linear|radial|conic)-gradient\(/i;
const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#abc" / "aabbcc" → "#aabbcc", lower case; anything else → null. */
function hexOf(value) {
  const m = HEX.exec(value);
  if (!m) return null;
  const h = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return `#${h.toLowerCase()}`;
}

/** The same hue, darker: each channel scaled by `factor`. */
function darker(hex, factor = 0.6) {
  const n = parseInt(hex.slice(1), 16);
  const ch = (shift) => Math.round(((n >> shift) & 0xff) * factor).toString(16).padStart(2, '0');
  return `#${ch(16)}${ch(8)}${ch(0)}`;
}

/** What to store for a colour a caller sent. */
export function normalizeBoardColor(value) {
  if (value == null) return null;
  const s = String(value).trim();
  if (!s) return null;
  if (GRADIENT.test(s)) return s;
  const hex = hexOf(s);
  return hex ? `linear-gradient(135deg, ${hex} 0%, ${darker(hex)} 100%)` : null;
}

/** What a tile, dot or row draws for a stored colour. */
export function boardBackground(color) {
  return normalizeBoardColor(color) || DEFAULT_BOARD_GRADIENT;
}
